import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ContextEDH } from "@context/ContextEDH";
import useContextUrl from "@common/hooks/useContextUrl";
import useModulesByCode from "@common/hooks/useModulesByCode";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import useGetCatalogs from "./useGetCatalogs";
import useBookingAccess from "@common/hooks/useBookingAccess";
import { ScenarioConfiguration } from "@models/ISchema";
import { useHistory } from "react-router-dom";

const isAccesible = (item: any) => {
  if (item?.id === "programming_structures") {
    return !!item?.permissions?.read;
  }

  return !!item?.accesible;
};

const getAccesibleUrls = (items: any[], darwinUrl: string, scenario: string, origin: string) => {
  return items?.filter?.(isAccesible)?.map(item => ({
    self: item,
    label: item?.label,
    onClick:
      item?.onClick ||
      (() => {
        window?.open(
          item?.url
            ? item?.url
            : `${darwinUrl}/${item?.path}?scenario_id=${scenario}${
                origin ? `&origin_id=${origin}` : ""
              }`,
          "_blank",
        );
      }),
  }));
};

const useGetModulesUrls = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const history = useHistory();
  const { state } = useContext(ContextEDH);
  const { modulesByCode } = useModulesByCode();
  const { origin, scenario } = useContextUrl();
  const { adaptedDarwinUrl, getProgrammingStructuresUrl } = useNavigationUrl();
  const { isBookingAccesible } = useBookingAccess();

  const isBaseDataLoaded = !!("id" in (state?.base?.base?.user ?? {}));
  const { catalogsOptions } = useGetCatalogs();

  const isEdhAccesible = useMemo(() => {
    return !!modulesByCode?.["scheduler_editor"]?.accesible;
  }, [modulesByCode]);

  const reportsUrls = useMemo(() => {
    const adaptedReports = state?.base?.base?.reports?.map(item => ({
      ...item,
      label: t(`home.action-cards.reports.options.${item.id}`, item?.label ?? ""),
    }));

    return getAccesibleUrls(adaptedReports, adaptedDarwinUrl, scenario, origin);
  }, [state?.base?.base?.reports, language, origin, scenario]);

  const scenariosConfigurationUrls = useMemo(() => {
    const albusCustomizedEquivalencesModule = modulesByCode?.["albus_customized_equivalences"];

    const adaptedScenarioConfigurations = state?.base?.base?.scenarioConfigurations?.map(item => ({
      ...item,
      label: t(
        `home.action-cards.configuration.scenario-configurations.${item.id}`,
        item?.label ?? "",
      ),
    })) as (ScenarioConfiguration & { url?: string })[];

    if (albusCustomizedEquivalencesModule?.url) {
      adaptedScenarioConfigurations.push({
        id: "albus_customized_equivalences",
        path: "",
        accesible: albusCustomizedEquivalencesModule?.accesible,
        label: t(
          "home.action-cards.configuration.scenario-configurations.albus-customized-equivalences",
        ),
        url: albusCustomizedEquivalencesModule?.url,
      });
    }

    return getAccesibleUrls(adaptedScenarioConfigurations, adaptedDarwinUrl, scenario, origin);
  }, [state?.base?.base?.scenarioConfigurations, language, origin, scenario]);

  const productConfigurationUrls = useMemo(() => {
    const adaptedProductConfigurations = state?.base?.base?.productConfigurations?.map(item => ({
      ...item,
      label: t(
        `home.action-cards.configuration.product-configurations.${item.id}-${item.path}`,
        item?.label ?? "",
      ),
      onClick:
        item?.id === "programming_structures"
          ? () => history.push(getProgrammingStructuresUrl())
          : undefined,
    }));

    return getAccesibleUrls(adaptedProductConfigurations, adaptedDarwinUrl, scenario, origin);
  }, [state?.base?.base?.productConfigurations, language, origin, scenario]);

  const userConfigurationUrls = useMemo(() => {
    const adaptedUserConfigurations = state?.base?.base?.userConfigurations?.map(item => ({
      ...item,
      label: t(`home.action-cards.user.options.${item.id}`, item?.label ?? ""),
    }));

    return getAccesibleUrls(adaptedUserConfigurations, adaptedDarwinUrl, scenario, origin);
  }, [state?.base?.base?.userConfigurations, language, origin, scenario]);

  const forecastsUrls = useMemo(() => {
    const adaptedForecasts = state?.base?.base?.forecasts?.map(item => ({
      ...item,
      label: t(`home.action-cards.optimization-process.forecasts.${item.id}`, item?.label ?? ""),
    }));

    const adaptedUrls = getAccesibleUrls(adaptedForecasts, adaptedDarwinUrl, scenario, origin);

    const demandEditor = modulesByCode?.["demand_editor"] ?? null;
    if (demandEditor && demandEditor?.accesible && demandEditor?.url) {
      adaptedUrls.push({
        label: t("home.action-cards.optimization-process.forecasts.editor-demands"),
        onClick: () => {
          window?.open(demandEditor?.url, "_blank");
        },
        self: demandEditor,
      });
    }

    return adaptedUrls;
  }, [state?.base?.base?.forecasts, language, origin, scenario, modulesByCode]);

  const assignationsUrls = useMemo(() => {
    const adaptedAssignations = state?.base?.base?.assignations?.map(item => ({
      ...item,
      label: t(
        `home.action-cards.optimization-process.assignations.${item.id}-${item.path}`,
        item?.label ?? "",
      ),
    }));

    return getAccesibleUrls(adaptedAssignations, adaptedDarwinUrl, scenario, origin);
  }, [state?.base?.base?.assignations, language, origin, scenario]);

  const massActionsUrls = useMemo(() => {
    const modules = [];
    const scheduleSeed = modulesByCode?.["mops_schedule_seed"];
    const subgroupCreation = modulesByCode?.["mops_subgroup_creation"];
    const groupInactivation = modulesByCode?.["mops_disabling_groups"];

    if (!!scheduleSeed && !!scheduleSeed?.accesible && !!scheduleSeed?.url) {
      modules.push({
        ...scheduleSeed,
        title: t("home.action-cards.mass-actions.btn-programming-load"),
        onClick: () => {
          window.open(scheduleSeed?.url, "_blank");
        },
      });
    }

    if (!!subgroupCreation && !!subgroupCreation?.accesible && !!subgroupCreation?.url) {
      modules.push({
        ...subgroupCreation,
        title: t("home.action-cards.mass-actions.btn-subgroup-creation"),
        onClick: () => {
          window.open(subgroupCreation?.url, "_blank");
        },
      });
    }

    if (!!groupInactivation && !!groupInactivation?.accesible && !!groupInactivation?.url) {
      modules.push({
        ...groupInactivation,
        title: t("home.action-cards.mass-actions.btn-group-inactivation"),
        onClick: () => {
          window.open(groupInactivation?.url, "_blank");
        },
      });
    }

    return modules;
  }, [modulesByCode, language]);

  const hasSomeModulesAccesibles = useMemo(() => {
    const hasReportsUrls = !!(reportsUrls ?? [])?.length;
    const hasScenariosConfigurationUrls = !!(scenariosConfigurationUrls ?? [])?.length;
    const hasProductConfigurationUrls = !!(productConfigurationUrls ?? [])?.length;
    const hasUserConfigurationUrls = !!(userConfigurationUrls ?? [])?.length;
    const hasForecastsUrls = !!(forecastsUrls ?? [])?.length;
    const hasAssignationsUrls = !!(assignationsUrls ?? [])?.length;
    const hasMassActionsUrls = !!(massActionsUrls ?? [])?.length;
    const hasAdminBookingAccess = isBookingAccesible && isEdhAccesible;

    return (
      hasReportsUrls ||
      hasScenariosConfigurationUrls ||
      hasProductConfigurationUrls ||
      hasUserConfigurationUrls ||
      hasForecastsUrls ||
      hasAssignationsUrls ||
      hasMassActionsUrls ||
      hasAdminBookingAccess ||
      isEdhAccesible
    );
  }, [
    reportsUrls,
    scenariosConfigurationUrls,
    productConfigurationUrls,
    userConfigurationUrls,
    forecastsUrls,
    assignationsUrls,
    massActionsUrls,
    isBookingAccesible,
    isEdhAccesible,
  ]);

  return {
    reportsUrls,
    scenariosConfigurationUrls,
    productConfigurationUrls,
    userConfigurationUrls,
    forecastsUrls,
    assignationsUrls,
    massActionsUrls,
    catalogsOptions,
    hasSomeModulesAccesibles,
    isBaseDataLoaded,
    canAccessEDH: isEdhAccesible && !!origin,
    isEdhAccesible,
    isBookingAccesible,
  };
};

export default useGetModulesUrls;
